import React, { useState, useEffect } from 'react';
import { FaEdit, FaTrash } from 'react-icons/fa';
import Pagination from '../components/Pagination';
import Loader from '../components/Loader';
import ConfirmModal from '../components/ConfirmModal';
import { useFetch } from '../hooks/useFetch';
import AddBirlikModal from '../components/AddBirlikModal';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';

const API_URL = '/api/birlik';

const Birliklar = () => {
  const { user, isAuthenticated } = useAuth();
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Modal and form state
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('add'); // 'add' or 'edit'
  const [editBirlik, setEditBirlik] = useState(null);

  // Confirm delete modal
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Fetch birliklar with proper authorization
  const { data, loading, error, fetchData } = useFetch(
    `${API_URL}?page=${currentPage}&limit=${itemsPerPage}`
  );

  useEffect(() => {
    fetchData();
  }, [currentPage, itemsPerPage]);

  // Handlers
  const handleOpenAdd = () => {
    setModalType('add');
    setEditBirlik(null);
    setShowModal(true);
  };

  const handleOpenEdit = (birlik) => {
    setModalType('edit');
    setEditBirlik(birlik);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditBirlik(null);
  };

  const handleSave = async (birlik) => {
    try {
      if (modalType === 'add') {
        await fetchData({
          method: 'POST',
          body: JSON.stringify({
            name: birlik.name
          })
        });
        toast.success("Birlik muvaffaqiyatli qo'shildi!");
      } else if (modalType === 'edit' && birlik.id) {
        // For edit, we need to use a different URL
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/${birlik.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            name: birlik.name
          })
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
        }
        
        toast.success('Birlik muvaffaqiyatli tahrirlandi!');
      }
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Xatolik yuz berdi!');
    }
  };

  const handleDelete = async () => {
    try {
      // For delete, we need to use a different URL
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/${deleteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
      
      setShowConfirm(false);
      setDeleteId(null);
      fetchData();
      toast.success("Birlik muvaffaqiyatli o'chirildi!");
    } catch (err) {
      toast.error(err.message || "O'chirishda xatolik!");
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowConfirm(true);
  };

  // Render
  return (
    <div className="container mt-4">
      {loading && !data ? (
        <Loader />
      ) : (
        <>
          <div className='d-flex justify-content-between mb-3'>
          <h1>Birliklar</h1>
          <button className="btn btn-success mb-3" onClick={handleOpenAdd}>
            + Birlik qo'shish
          </button>
          </div>
          {data?.items?.length > 0 && (
            <div className="table-responsive rounded-4 shadow">
              <table className="table table-striped table-hover mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Nomi</th>
                    <th>Amallar</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((birlik, idx) => (
                    <tr key={birlik.id}>
                      <td>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                      <td>{birlik.name}</td>
                      <td>
                        <button 
                          className="btn btn-sm btn-warning me-2" 
                          onClick={() => handleOpenEdit(birlik)}
                          title="Tahrirlash"
                        >
                          <FaEdit />
                        </button>
                        <button 
                          className="btn btn-sm btn-danger" 
                          onClick={() => handleDeleteClick(birlik.id)}
                          title="O'chirish"
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {data?.items?.length === 0 && (
            <div className="alert alert-info text-center">Ma'lumot topilmadi</div>
          )}
          <Pagination
            currentPage={currentPage}
            totalItems={data.total || 0}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(n) => { 
              setItemsPerPage(n); 
              setCurrentPage(1); 
            }}
          />
        </>
      )}
      {/* Add/Edit Modal */}
      <AddBirlikModal
        show={showModal}
        onClose={handleCloseModal}
        onSave={handleSave}
        isEditMode={modalType === 'edit'}
        birlikToEdit={editBirlik}
      />

      {/* Confirm Delete Modal */}
      <ConfirmModal
        show={showConfirm}
        onConfirm={handleDelete}
        onCancel={() => { 
          setShowConfirm(false); 
          setDeleteId(null); 
        }}
        message="Ushbu birlikni o'chirishni tasdiqlaysizmi?"
      />
    </div>
  );
};

export default Birliklar; 